import { generateVerifierAndChallenge } from "~/auth/PKCEClient";
import Config from "~/config";

const authUrl = Config.authUrl;
const sfApiUrl = Config.sfApiUrl;
const clientId = Config.clientId;
const tenantId = Config.tenantId;

export class SFAuth {
	private static _instance: SFAuth;

	private currentAccessToken: Maybe<string> = null;
	private currentRefreshToken: Maybe<string> = null;

	// Singleton
	private constructor() {}

	public static get Instance() {
		return this._instance || (this._instance = new this());
	}

	async checkSession(forceRenewToken?: boolean): Promise<string> {
		const tokens = this.getCurrentTokens();
		if (tokens === null) {
			return "";
		}

		try {
			const freshTokens = await this.refreshPKCESessionIfNeeded(tokens, forceRenewToken || false);

			this.currentAccessToken = freshTokens.accessToken;
			this.currentRefreshToken = freshTokens.refreshToken;
			window.localStorage.setItem("appTokens", JSON.stringify(freshTokens));
			return freshTokens.accessToken;
		} catch (e) {
			console.log("Error parsing tokens: ", e);
			return "";
		}
	}

	/** Return current tokens stored in memory if available. Else retrieve from
	 * localStorage. If not available, return null. */
	private getCurrentTokens(): Maybe<TokenSet> {
		let tokens: Maybe<TokenSet> = null;

		if (this.currentAccessToken && this.currentRefreshToken) {
			tokens = {
				accessToken: this.currentAccessToken,
				refreshToken: this.currentRefreshToken,
			};
		} else {
			const tokenStr = window.localStorage.getItem("appTokens");
			if (tokenStr) {
				tokens = JSON.parse(tokenStr);
			}
		}

		return tokens;
	}

	/** Returns a `Promise<boolean>` for whether or not valid tokens are stored (transparently renews
	 * access token if it is at/near expiry) */
	private async refreshPKCESessionIfNeeded(tokens: TokenSet, forceRenewToken?: boolean): Promise<TokenSet> {
		let freshTokens = { ...tokens };

		// Refresh if access token expired
		if (this.isTokenNearExpiry(tokens.accessToken) || forceRenewToken || false) {
			freshTokens = await this.refreshToken(tokens.accessToken, tokens.refreshToken);
		}

		return freshTokens;
	}

	private async refreshToken(accessToken: string, refreshToken: string): Promise<JWTRefreshResponse> {
		const url = `${authUrl}/api/jwt/refresh`;

		const response = await fetch(url, {
			body: JSON.stringify({
				refreshToken,
				token: accessToken,
			}),
			headers: {
				"Content-Type": "application/json",
			},
			method: "POST",
		});

		if (!response.ok) {
			throw new Error(response.statusText);
		}

		const data = await response.json();
		return {
			accessToken: data.token,
			refreshToken: data.refreshToken,
		};
	}

	parseTokenPayload(accessToken: string) {
		const base64Url = accessToken.split(".")[1];
		const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
		const jsonPayload = decodeURIComponent(
			window
				.atob(base64)
				.split("")
				.map(function (c) {
					return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
				})
				.join("")
		);

		return JSON.parse(jsonPayload);
	}

	// Return true if access token is within `thresholdPercentage` of expiry relative to token lifetime
	// eg if thresholdPercentage is 20, and token lifetime is 3600 seconds, then if the token has less than
	// 720 seconds (20% of 3600 seconds) remaining, it is considered near expiry.
	isTokenNearExpiry(accessToken: string, thresholdPercentage: number = 20): boolean {
		const payload = this.parseTokenPayload(accessToken);
		// expiry and issued at timestamps in seconds
		const { exp, iat } = payload;

		const currentTime = Date.now() / 1000; // Convert to seconds
		const timeRemaining = exp - currentTime;
		const tokenLifetime = exp - iat; // Calculate token lifetime

		// Calculate the threshold time as a percentage of token lifetime
		const thresholdTime = (thresholdPercentage / 100) * tokenLifetime;

		// Check if the remaining time is less than or equal to the threshold time
		const nearExpiry = timeRemaining <= thresholdTime;

		return nearExpiry;
	}
	async exchangePKCECodeForTokens(code: string): Promise<void> {
		const verifier = window.localStorage.getItem("pkceVerifier");
		if (verifier === null) {
			throw new Error("No PKCE verifier found.");
		}

		const data = new URLSearchParams();
		data.append("client_id", clientId);
		data.append("code", code);
		data.append("code_verifier", verifier);
		data.append("grant_type", "authorization_code");
		// For PKCE, redirect is directly back to app origin
		data.append("redirect_uri", `${window.location.origin}/oauth-redirect`);

		const response = await fetch(`${authUrl}/oauth2/token`, {
			body: data,
			headers: {
				"Content-Type": "application/x-www-form-urlencoded",
			},
			method: "POST",
		});

		if (response.status !== 200) {
			const text = await response.text();
			if (process.env.NODE_ENV === "development") {
				console.log("DEBUG Response: ", response);
				console.log("DEBUG Text: ", text);
			}
			const err = new Error("Error exchanging code for tokens");
			throw err;
		}

		const tokensResponse = await response.json();
		const tokens = {
			accessToken: tokensResponse.access_token,
			refreshToken: tokensResponse.refresh_token,
		};

		window.localStorage.setItem("appTokens", JSON.stringify(tokens));
		window.localStorage.removeItem("pkceVerifier");

		this.currentAccessToken = tokens.accessToken;
		return;
	}

	async getCurrentUser(): Promise<Maybe<SFUser>> {
		let fetchOptions: RequestInit = {};
		fetchOptions = {
			headers: {
				Authorization: `Bearer ${this.currentAccessToken}`,
			},
		};
		const response = await fetch(`${sfApiUrl}/me`, fetchOptions);

		if (response.status === 200) {
			const userInfo: SFUser = await response.json();
			return userInfo;
		}

		if (response.status === 401) {
			return null;
		}

		const err = new Error("Error checking session");
		throw err;
	}

	async getCompany(): Promise<Maybe<SFCompany>> {
		let fetchOptions: RequestInit = {};

		fetchOptions = {
			headers: {
				Authorization: `Bearer ${this.currentAccessToken}`,
			},
		};

		const response = await fetch(`${sfApiUrl}/company`, fetchOptions);

		if (response.status === 200) {
			const company: SFCompany = await response.json();
			return company;
		}

		if (response.status === 401) {
			return null;
		}

		const err = new Error("Error fetching company.");
		throw err;
	}

	async getProfilesForProduct(productName: string): Promise<Maybe<SFAppProfile[]>> {
		const url = `${sfApiUrl}/profiles?productName=${productName}`;
		const response = await fetch(url, {
			headers: {
				Authorization: `Bearer ${this.currentAccessToken}`,
			},
		});

		if (response.status === 200) {
			const profiles: SFAppProfile[] = await response.json();
			return profiles;
		}

		if (response.status === 401) {
			return null;
		}

		const err = new Error("Error checking session");
		throw err;
	}

	async getMobileAppsProfiles(): Promise<Maybe<MobileAppsProfile[]>> {
		const profiles = await this.getProfilesForProduct("mobile");
		if (!profiles) {
			return null;
		}

		return profiles as MobileAppsProfile[];
	}

	async redirectToLogin(onSuccessReturnToPath = true) {
		// Just came back from login attempt, don't redirect again
		if (window.location.pathname.startsWith("/oauth-redirect")) {
			return;
		}

		const queryparams: Record<string, string> = {
			client_id: clientId,
			// redirect_uri: `${redirectUri}${port ? `:${port}` : ''}/oauth-redirect`,
			redirect_uri: `${window.location.origin}/oauth-redirect`,
			response_type: "code",
			scope: "openid profile email offline_access",
		};

		const authState = {
			// Not used with PKCE
			appName: undefined,

			// ** Optional **
			// On dev, a port can be provided so that the redirect returns the user
			// back to the correct port after successful authentication.
			port: window.location.port,

			// ** Optional **
			// By default, return to current path after authentication; else return to root
			returnTo: onSuccessReturnToPath ? window.location.pathname : "/",
		};

		const { challenge, verifier } = await generateVerifierAndChallenge();
		window.localStorage.setItem("pkceVerifier", verifier);
		queryparams.code_challenge_method = "S256";
		queryparams.code_challenge = challenge;
		queryparams.state = JSON.stringify(authState);
		const params = new URLSearchParams(queryparams);
		const qs = params.toString();
		window.location.href = `${authUrl}/oauth2/authorize?${qs}`;
	}

	redirectToLogout() {
		const params = new URLSearchParams({
			client_id: clientId,
			state: JSON.stringify({
				// ** Optional **
				// On dev, a port can be provided so that the redirect returns the user
				// back to the correct port after logging out.
				port: window.location.port,
			}),
			tenantId: tenantId,
		});
		const qs = params.toString();
		const logoutUrl = `${authUrl}/oauth2/logout?${qs}`;
		window.localStorage.removeItem("appTokens");
		window.location.href = logoutUrl;
	}
}

const sfAuth = SFAuth.Instance;

export default sfAuth;
